<template>
  <!-- What a tap on a live game opens on the Picks page: its
       chance to cover through the game so far (CoverChart), where it started,
       and how low and high it has been. Shared by Your Picks' live lanes and
       the games list, so the two read the same. -->
  <div class="rounded-xl border border-white/10 bg-white/[0.03] p-3">
    <p v-if="loading" class="py-6 text-center text-[11px] font-semibold text-slate-400">Loading scores…</p>
    <template v-else>
      <!-- The answer first: each side's chance to cover its frozen line right
           now, then what a touchdown either way would do to the first. -->
      <div v-if="sides" class="mb-2.5">
        <div class="flex items-end justify-between gap-3">
          <p v-for="(s, i) in sides" :key="s.abbr" :class="i ? 'text-right' : ''">
            <span class="block text-[10px] font-extrabold tracking-[0.12em] text-slate-400">{{ s.abbr }} {{ s.line }}</span>
            <span class="text-[26px] font-black leading-none tracking-tight tabular-nums" :class="s.p == null ? 'text-slate-400' : s.p >= 0.5 ? 'text-emerald-400' : 'text-rose-400'">{{ s.p == null ? "—" : chance(s.p) }}</span>
            <span class="ml-1 text-[11px] font-semibold text-slate-400">to cover</span>
          </p>
        </div>
        <p v-if="whatIf" class="mt-1.5 text-[10px] font-semibold text-slate-400">{{ whatIf }}</p>
      </div>
      <CoverChart v-if="path && path.length > 1" :path="path" :tone="tone" />
      <p v-else class="py-3 text-center text-[11px] font-semibold text-slate-400">No chart yet: ESPN has no scores for this game.</p>
      <div class="mt-2 flex flex-wrap gap-1.5">
        <span v-if="now != null && !sides" class="chip !text-amber-300">Now {{ chance(now) }}</span>
        <span v-if="pregame != null" class="chip">Pre-game {{ percent1(pregame) }}<template v-if="band"> · {{ band }}</template></span>
        <template v-if="range">
          <span class="chip">Lowest {{ chance(range.low) }}</span>
          <span class="chip">Highest {{ chance(range.high) }}</span>
        </template>
      </div>
      <slot />
    </template>
  </div>
</template>

<script>
import CoverChart from "./CoverChart.vue";
import { formatChance } from "@/utils/liveCover.js";

export default {
  name: "CoverDetail",
  components: { CoverChart },
  props: {
    // [{ minute, p }] from sideCoverPath; null while loading or without plays.
    path: { type: Array, default: null },
    // "live", or "win" / "loss" once final.
    tone: { type: String, default: "live" },
    // The live chance right now; null once final.
    now: { type: Number, default: null },
    pregame: { type: Number, default: null },
    band: { type: String, default: null },
    loading: { type: Boolean, default: false },
    // [{ abbr, line, p }] -- the row's side, then its opponent, the same size.
    // Leads the panel with the numbers when given.
    sides: { type: Array, default: null },
    whatIf: { type: String, default: null },
  },
  computed: {
    // Lowest and highest so far. Once final the last point is the result
    // (0 or 100%), which is not a chance it ever had, so it is left out.
    range() {
      const path = this.path;
      if (!path || path.length < 2) return null;
      const values = (this.tone === "live" ? path : path.slice(0, -1)).map((point) => point.p);
      return { low: Math.min(...values), high: Math.max(...values) };
    },
  },
  methods: {
    chance(p) {
      return formatChance(p);
    },
    percent1(p) {
      return `${(p * 100).toFixed(1)}%`;
    },
  },
};
</script>

<style scoped>
.chip { border-radius: 9999px; background: rgb(255 255 255 / 0.06); padding: 0.25rem 0.5rem; color: #cbd5e1; font-size: 10px; font-weight: 700; white-space: nowrap; }
</style>
